import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Broadcast, Copy, Trash, Plus, CircleNotch, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const CLIENT_TIMEOUT_MS = 45000;

export const ClientDiscoveryPanel = ({ keyword, onCandidates, onServerFallback }) => {
  const { t } = useTranslation();
  const [agents, setAgents] = useState([]);
  const [online, setOnline] = useState(false);
  const [newToken, setNewToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const pollRef = useRef(null);
  const startRef = useRef(0);

  const loadAgents = async () => {
    try {
      const { data } = await api.get("/product-hunter/client/agents");
      setAgents(data.agents || []);
      setOnline(!!(data.status && data.status.online));
    } catch { /* ignore */ }
  };
  useEffect(() => { loadAgents(); const i = setInterval(loadAgents, 10000); return () => clearInterval(i); }, []);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/product-hunter/client/agents", { label: "Client Discovery" });
      setNewToken(data.token);
      toast.success(t("ph.clientDisc.tokenCreated"));
      loadAgents();
    } catch { toast.error(t("ph.clientDisc.tokenError")); }
    finally { setBusy(false); }
  };

  const revoke = async (id) => {
    setBusy(true);
    try { await api.delete(`/product-hunter/client/agents/${id}`); setNewToken(""); loadAgents(); }
    catch { toast.error(t("ph.clientDisc.revokeError")); }
    finally { setBusy(false); }
  };

  const copyToken = () => { navigator.clipboard?.writeText(newToken); toast.success(t("ph.clientDisc.copied")); };

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const runClientHunt = async () => {
    const kw = (keyword || "").trim();
    if (!kw) { toast.error(t("ph.enterRequest")); return; }
    stopPoll(); setTimedOut(false); setRunning(true);
    setStatusMsg(t("ph.clientDisc.waiting"));
    let requestId;
    try {
      const { data } = await api.post("/product-hunter/client/hunt", { request: kw });
      if (data.status === "failed") { setRunning(false); toast.error(t("ph.huntFailed", { error: data.error })); return; }
      requestId = data.request_id;
    } catch { setRunning(false); toast.error(t("ph.huntError")); return; }
    startRef.current = Date.now();
    pollRef.current = setInterval(async () => {
      if (Date.now() - startRef.current > CLIENT_TIMEOUT_MS) {
        stopPoll(); setRunning(false); setTimedOut(true);
        setStatusMsg(t("ph.clientDisc.timeout"));
        return;
      }
      try {
        const { data: req } = await api.get(`/product-hunter/requests/${requestId}`);
        if (req.status === "completed") {
          stopPoll(); setRunning(false);
          const { data: cres } = await api.get(`/product-hunter/candidates?request_id=${requestId}`);
          const found = cres.candidates || [];
          onCandidates && onCandidates(found, found.length
            ? t("ph.huntDone", { count: found.length }) : t("ph.noResultsFound"));
          setStatusMsg(found.length ? t("ph.huntDone", { count: found.length }) : t("ph.noResultsFound"));
        } else if (req.status === "failed") {
          stopPoll(); setRunning(false);
          const code = req.error_code || (req.error || "").replace(/^client:|^source_error:/, "");
          const msg = t(`ph.sourceErr.${code}`, { defaultValue: t("ph.huntFailed", { error: req.error || "" }) });
          setStatusMsg(msg); toast.error(msg);
        }
      } catch { /* keep polling */ }
    }, 2000);
  };

  return (
    <div className="bg-card border border-border rounded-sm p-6" data-testid="cd-panel">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <Broadcast size={18} className="text-primary" weight="fill" />
        <h3 className="font-heading text-base font-bold">{t("ph.clientDisc.title")}</h3>
        <Badge variant="outline" className="rounded-sm text-[10px]" data-testid="cd-agent-status"
          data-online={online ? "1" : "0"}>
          {online ? t("ph.clientDisc.extOnline") : t("ph.clientDisc.extOffline")}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground mb-4 max-w-2xl">{t("ph.clientDisc.helper")}</p>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <Button size="sm" variant="outline" className="rounded-sm" disabled={busy}
          onClick={generate} data-testid="cd-generate-token">
          <Plus size={15} className="mr-1.5" />{t("ph.clientDisc.generate")}
        </Button>
        {agents.map((a) => (
          <span key={a.id} className="flex items-center gap-1 border border-border rounded-sm px-2 py-1 text-xs"
            data-testid={`cd-agent-${a.id}`}>
            <code className="text-[11px]">{a.prefix}…</code>
            <button className="text-destructive" onClick={() => revoke(a.id)} data-testid={`cd-revoke-${a.id}`}>
              <Trash size={13} />
            </button>
          </span>
        ))}
      </div>

      {newToken && (
        <div className="border border-dashed border-primary/40 rounded-sm p-3 mb-4 bg-primary/5" data-testid="cd-token-box">
          <p className="text-xs text-muted-foreground mb-1">{t("ph.clientDisc.tokenOnce")}</p>
          <div className="flex items-center gap-2">
            <code className="text-xs break-all flex-1" data-testid="cd-token-value">{newToken}</code>
            <Button size="sm" variant="ghost" className="rounded-sm" onClick={copyToken} data-testid="cd-copy-token">
              <Copy size={14} />
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" className="rounded-sm" disabled={running} onClick={runClientHunt}
          data-testid="cd-run-client-hunt">
          {running ? <CircleNotch size={15} className="mr-1.5 animate-spin" /> : <Broadcast size={15} className="mr-1.5" />}
          {t("ph.clientDisc.runHunt")}
        </Button>
        {statusMsg && (
          <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="cd-status">
            {running ? <CircleNotch size={13} className="animate-spin" /> : <CheckCircle size={13} />}{statusMsg}
          </span>
        )}
      </div>

      {timedOut && (
        <div className="mt-4 border border-amber-500/40 bg-amber-500/5 rounded-sm p-3" data-testid="cd-timeout-actions">
          <p className="text-sm flex items-center gap-2 mb-2">
            <WarningCircle size={16} className="text-amber-500" />{t("ph.clientDisc.timeoutMsg")}
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="rounded-sm" onClick={runClientHunt} data-testid="cd-retry">
              {t("ph.clientDisc.retry")}
            </Button>
            <Button size="sm" variant="outline" className="rounded-sm" data-testid="cd-use-server"
              onClick={() => { setTimedOut(false); onServerFallback && onServerFallback(keyword); }}>
              {t("ph.clientDisc.useServer")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDiscoveryPanel;
